/// <reference types="vite-plugin-svgr/client" />

import React, { useEffect, useRef, useState } from 'react';
import { DesktopNavigation } from './DesktopNavigation';
import { MobileNavigation } from './MobileNavigation';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { useAuth } from '../../context/auth';
import { useNotificationStore } from '../../stores/notificationStore';
import { useProfileStore } from '../../stores/profileStore';

const NOTIFICATION_REFRESH_INTERVAL = 1 * 60 * 1000;

export const Navigation: React.FC = () => {
  const { t } = useTranslation('common');
  const { user, signOut } = useAuth();
  const { profile } = useProfileStore();
  const navigate = useNavigate();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  useEffect(() => {
    if (profile) {
      fetchUnreadCount();
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, NOTIFICATION_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [profile, fetchUnreadCount]);

  const isMenuOpen = isAccountMenuOpen || isSearchMenuOpen;

  const closeMenus = () => {
    setIsAccountMenuOpen(false);
    setIsSearchMenuOpen(false);
  };

  // Click-away handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenus();
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleSignOut = () => {
    signOut();
    closeMenus();
    navigate('/');
  };

  const mobileMenuItemClass =
    "text-primary-inverse text-center text-xl font-[900] leading-[1.875rem] font-ambit md:text-base md:text-gray-600 md:font-normal md:hover:text-gray-900 [font-feature-settings:'liga'_off,'clig'_off] hover:cursor-pointer";

  const renderNavLinks = (variant: 'alternate' | 'primary') => (
    <>
      <Link to="/about" className={mobileMenuItemClass} onClick={() => closeMenus()}>
        {t('navigation.about')}
      </Link>
      <Link to="/community" className={mobileMenuItemClass} onClick={() => closeMenus()}>
        {t('navigation.community')}
      </Link>
      <Link to="/explore" className={mobileMenuItemClass} onClick={() => closeMenus()}>
        {t('navigation.explore')}
      </Link>

      {!user && (
        <>
          <Link to="/login" className={mobileMenuItemClass} onClick={() => closeMenus()}>
            {t('navigation.signIn')}
          </Link>
          <Button href="/signup" onClick={() => closeMenus()} variant={variant}>
            {t('navigation.signUp')}
          </Button>
        </>
      )}
    </>
  );

  return (
    <nav className="px-4 pt-6 md:px-8 md:pt-4 md:px-8 relative" ref={menuRef}>
      <DesktopNavigation
        isAccountMenuOpen={isAccountMenuOpen}
        onSignOut={handleSignOut}
        onAccountMenuToggle={(forceClose = false) =>
          setIsAccountMenuOpen(forceClose ? false : !isAccountMenuOpen)
        }
        onCloseMenu={closeMenus}
        notifications={unreadCount}
      />

      {/* Mobile Navigation */}
      <MobileNavigation
        isOpen={isAccountMenuOpen}
        onToggle={(forceClose = false) =>
          setIsAccountMenuOpen(forceClose ? false : !isAccountMenuOpen)
        }
        onSignOut={handleSignOut}
        notifications={unreadCount}
        renderNavLinks={renderNavLinks}
      />
    </nav>
  );
};

export default Navigation;
